import { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { EmptyState } from '../atoms/EmptyState';
import { Grid } from '../atoms/Grid/Grid';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { TableWrap } from '../budget/BudgetPageParts';
import { monthLabel } from '../budget/budgetFormat';
import { useMoney } from '../budget/useMoney';
import { useToast } from '../../stores/toastStore';
import type { EnergySummary } from '../../api/controller';
import { useEnergySummary, useSetEnergyPrice } from '../../hooks/useController';
import { formatPricePerKwh, formatValue, parsePricePerKwh, priceInputValue } from './controllerFormat';
import './Controller.css';

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card variant="outlined">
      <Stack gap="1">
        <Text color="muted" size="sm">{label}</Text>
        <Text size="xl" weight="semibold">{value}</Text>
      </Stack>
    </Card>
  );
}

function PriceForm({ aquariumId, price }: { aquariumId: string; price: number | null }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const money = useMoney();
  const { showToast } = useToast();
  const id = useId();
  const save = useSetEnergyPrice(aquariumId);
  const [value, setValue] = useState(() => priceInputValue(price, language));
  const [touched, setTouched] = useState(false);
  useEffect(() => setValue(priceInputValue(price, language)), [price, language]);

  const parsed = value.trim() === '' ? null : parsePricePerKwh(value);
  const invalid = value.trim() !== '' && parsed === null;

  return (
    <form
      noValidate
      className="controller-energy__price"
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        if (invalid || save.isPending) return;
        save.mutate(parsed, { onSuccess: () => showToast(t('controller.energy.priceSaved'), 'success') });
      }}
    >
      <Input
        id={`${id}-price`}
        label={t('controller.energy.price', { currency: money.symbol })}
        helperText={t('controller.energy.priceHint')}
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        error={touched && invalid ? t('controller.energy.priceError') : undefined}
      />
      <Button type="submit" variant="secondary" disabled={save.isPending} aria-busy={save.isPending}>
        {t('controller.energy.savePrice')}
      </Button>
      {save.isError && <Alert>{t('controller.common.saveError')}</Alert>}
    </form>
  );
}

function EnergyFigures({ summary }: { summary: EnergySummary }) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const money = useMoney();
  const kwh = (value: number) => formatValue(value, 'kWh', language, 1);
  const caption = t('controller.energy.tableCaption', { year: summary.year });
  const months = summary.months.filter((m) => m.kwh > 0);

  return (
    <Stack gap="4">
      <Grid minItemWidth="sm">
        <Kpi label={t('controller.energy.total', { year: summary.year })} value={kwh(summary.totalKwh)} />
        {summary.totalCostCents !== null && (
          <Kpi label={t('controller.energy.cost', { year: summary.year })} value={money.format(summary.totalCostCents)} />
        )}
        {summary.averageWatts !== null && (
          <Kpi label={t('controller.energy.average')} value={formatValue(summary.averageWatts, 'W', language, 0)} />
        )}
        {summary.projectedKwh !== null && (
          <Kpi
            label={t('controller.energy.projected', { year: summary.year })}
            value={summary.projectedCostCents !== null ? `${kwh(summary.projectedKwh)} · ${money.format(summary.projectedCostCents)}` : kwh(summary.projectedKwh)}
          />
        )}
      </Grid>
      {summary.pricePerKwhCents === null && <Text color="muted">{t('controller.energy.noPrice')}</Text>}
      <TableWrap label={caption}>
        <table className="controller-table" data-testid="controller-energy-table">
          <caption className="controller-table__caption">{caption}</caption>
          <thead>
            <tr>
              <th scope="col">{t('controller.energy.month')}</th>
              <th scope="col" className="controller-table__num">{t('controller.energy.kwh')}</th>
              {summary.pricePerKwhCents !== null && <th scope="col" className="controller-table__num">{t('controller.energy.costColumn')}</th>}
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month}>
                <th scope="row">{monthLabel(m.month, language, 'long')}</th>
                <td className="controller-table__num">{kwh(m.kwh)}</td>
                {summary.pricePerKwhCents !== null && (
                  <td className="controller-table__num">{m.costCents !== null ? money.format(m.costCents) : '–'}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </Stack>
  );
}

export interface EnergyOverviewProps {
  aquariumId: string;
  year: number;
  /** Shows the price form (controller settings); the budget page shows the figures only. */
  editable?: boolean;
  /** Link target when not editable (to the controller settings). */
  manageHref?: string;
}

/**
 * Measured electricity (#1899): kWh and cost per month from the keys that
 * count toward the cost. Measured, not booked — it is not part of the budget's
 * expense totals.
 */
export function EnergyOverview({ aquariumId, year, editable = false, manageHref }: EnergyOverviewProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'de';
  const money = useMoney();
  const summary = useEnergySummary(aquariumId, year);

  if (summary.isPending) {
    return (
      <Card>
        <LiveRegion busy>
          <Text color="muted">{t('controller.common.loading')}</Text>
        </LiveRegion>
      </Card>
    );
  }
  if (summary.isError) {
    return (
      <LoadErrorState
        title={t('controller.energy.loadError')}
        titleLevel={3}
        onRetry={() => void summary.refetch()}
        retryLabel={t('controller.common.retry')}
      />
    );
  }

  const data = summary.data;
  return (
    <Card>
      <Stack gap="4">
        {editable && <PriceForm aquariumId={aquariumId} price={data.pricePerKwhCents} />}
        {!editable && data.pricePerKwhCents !== null && (
          <Text color="muted">
            {t('controller.energy.priceInfo', { price: formatPricePerKwh(data.pricePerKwhCents, language, money.currency) })}
          </Text>
        )}
        {!data.configured ? (
          <EmptyState
            icon={<Zap aria-hidden="true" />}
            headline={t('controller.energy.notConfiguredTitle')}
            description={t('controller.energy.notConfigured')}
            headingLevel={3}
          />
        ) : !data.hasData ? (
          <Text color="muted">{t('controller.energy.noData', { year })}</Text>
        ) : (
          <EnergyFigures summary={data} />
        )}
        <Text color="muted" size="sm">{t('controller.energy.budgetNote')}</Text>
        {manageHref && (
          <Inline>
            <Link className="controller-link" to={manageHref}>
              {t('controller.energy.manage')}
            </Link>
          </Inline>
        )}
      </Stack>
    </Card>
  );
}
